import { getGraphics } from '../graphics';
import { loadSave } from '../persist';
import type { SaveFile } from '../persist';
import { useStore } from '../store';

/**
 * "Report a problem": everything needed to replay a player's game exactly,
 * as a file they choose to share. Nothing is sent anywhere automatically.
 *
 * A local game is its save (config with seed, every move, bot RNG), which
 * replays deterministically. An online game is its code; the moves and seed
 * are on the server (see docs/bug-reports.md).
 */
export interface ProblemReport {
  kind: 'horde-problem-report';
  version: 1;
  createdAt: string;
  build: string;
  device: {
    userAgent: string;
    platform: string;
    screen: string;
    viewport: string;
    pixelRatio: number;
    memoryGb: number | null;
    gpu: string | null;
  };
  settings: { graphics: string; fastBots: boolean };
  local: SaveFile | null;
  online: { code: string; gameId: string; seat: string | null; appliedSeq: number } | null;
  screen: { name: string; replaying: boolean; error: string | null };
  /** The last lines of the game log, as the player saw them. */
  log: string[];
  /** Uncaught errors since the page loaded. */
  errors: string[];
}

const MAX_ERRORS = 20;
const errors: string[] = [];

/** Keep a short list of uncaught errors so a report can include them. Call once at startup. */
export function captureErrors(): void {
  const push = (text: string) => {
    errors.push(`${new Date().toISOString()} ${text}`.slice(0, 2000));
    if (errors.length > MAX_ERRORS) errors.shift();
  };
  window.addEventListener('error', (e) => {
    push(
      `${e.message} at ${e.filename}:${e.lineno}:${e.colno}${e.error?.stack ? `\n${e.error.stack}` : ''}`,
    );
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason as { stack?: string } | undefined;
    push(`Unhandled rejection: ${r?.stack ?? String(e.reason)}`);
  });
}

/** The GPU name, which matters for 3D bugs. Null where the browser hides it. */
function gpuName(): string | null {
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    if (!gl) return null;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const name = gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER) as string;
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return name;
  } catch {
    return null;
  }
}

export function buildReport(): ProblemReport {
  const st = useStore.getState();
  const o = st.online;
  return {
    kind: 'horde-problem-report',
    version: 1,
    createdAt: new Date().toISOString(),
    build: __BUILD__,
    device: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screen: `${screen.width}x${screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      pixelRatio: window.devicePixelRatio,
      memoryGb: (navigator as { deviceMemory?: number }).deviceMemory ?? null,
      gpu: gpuName(),
    },
    settings: { graphics: getGraphics(), fastBots: st.fast },
    local: o ? null : loadSave(),
    online: o
      ? { code: o.code, gameId: o.gameId, seat: o.myPlayerId, appliedSeq: o.appliedSeq }
      : null,
    screen: { name: st.screen, replaying: st.replaying, error: st.error },
    log: st.lines.slice(-40).map((l) => `R${l.round} ${l.text}`),
    errors: [...errors],
  };
}

/**
 * Hand the report to the player: the share sheet where it can take a file
 * (iOS, Android), otherwise a download. Returns what happened, for the UI.
 */
export async function shareReport(): Promise<'shared' | 'downloaded' | 'cancelled'> {
  const report = buildReport();
  const stamp = report.createdAt.replace(/[:.]/g, '-').slice(0, 19);
  const name = `horde-report-${stamp}.json`;
  const blob = new Blob([JSON.stringify(report, null, 1)], { type: 'application/json' });
  const file = new File([blob], name, { type: 'application/json' });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'Horde problem report' });
      return 'shared';
    } catch (e) {
      if ((e as Error).name === 'AbortError') return 'cancelled';
      // Share failed for another reason: fall back to a download.
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
}
